import { sortBy } from "lodash";
import { useRef, useState } from "react";

import { stepNames } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { ProgramForm } from "../types";
import useProgramFormLoadPreview from "../use-program-form-load-preview";
import {
  LoadPreview,
  defaultHourlyCost,
  defaultSecondsPerQuestion,
  defaultToolSavingsPercent,
  formatCount,
  formatDuration,
  formatHours,
  formatMoney,
  parseHourlyCost,
  parseMinutesPerQuestion,
  parsePercent,
  savingsSums,
  toolHelpedAnswers,
  writerToolHelpedQuestions,
} from "./readiness-figures";
import ReadinessIntegrations from "./readiness-integrations";
import { useReadinessTools } from "./readiness-tools";

/**
 * A guess the admin can change, written into the sentence it is part of. Text
 * rather than a number field, so it has no up and down arrows; the keypad on a
 * phone still offers numbers.
 *
 * With `restoreOnBlur`, while the box holds something that is not a value, such
 * as nothing or 0, the figures keep the last one that was, and leaving the box
 * puts that one back in it. Without it, an empty box is passed on as null.
 */
const AssumptionBox = ({
  value,
  parse,
  format,
  onChange,
  ariaLabel,
  restoreOnBlur = false,
  prefix,
  suffix,
  unitsOutside = false,
}: {
  value: number | null;
  parse: (text: string) => number | null;
  format: (value: number) => string;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  restoreOnBlur?: boolean;
  /** A unit shown before the number, such as "$". */
  prefix?: string;
  /** A unit shown after the number, such as "min". */
  suffix?: string;
  /** Leave the units as plain text beside the highlight, not inside it. */
  unitsOutside?: boolean;
}) => {
  const [text, setText] = useState(value === null ? "" : format(value));
  // Set on focus and cleared by the mouse-up that follows it: the browser would
  // otherwise turn that click into a caret and drop the selection.
  const justFocused = useRef(false);
  const highlight =
    "inline-flex items-baseline rounded bg-gray-100 px-1.5 font-medium text-gray-900 hover:bg-gray-200 focus-within:bg-blue-50 focus-within:ring-1 focus-within:ring-blue-300";
  const input = (
    <input
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      className="border-0 bg-transparent p-0 text-sm font-medium text-gray-900 tabular-nums shadow-none outline-none focus:ring-0"
      // As wide as what is typed, so the highlight hugs the number.
      style={{ width: `${Math.max(text.length, 1)}ch` }}
      value={text}
      // The whole value is selected on the way in, so typing replaces it.
      onFocus={(event) => {
        event.currentTarget.select();
        justFocused.current = true;
      }}
      onMouseUp={(event) => {
        if (justFocused.current) {
          event.preventDefault();
          justFocused.current = false;
        }
      }}
      onChange={(event) => {
        setText(event.target.value);
        const parsed = parse(event.target.value);
        if (parsed !== null || !restoreOnBlur) {
          onChange(parsed);
        }
      }}
      onBlur={
        restoreOnBlur && value !== null
          ? () => setText(format(value))
          : undefined
      }
    />
  );
  // Reads as a word in the sentence, not a form field: no border, a gray
  // highlight to say it can change, as wide as what is in it. The units sit
  // inside the highlight, or beside it as plain text.
  if (unitsOutside) {
    return (
      // A little room either side, so the units read as words beside the
      // value rather than part of it.
      <span className="inline-flex items-baseline gap-x-1">
        {prefix}
        <span className={highlight}>{input}</span>
        {suffix?.trim()}
      </span>
    );
  }
  return (
    <span className={highlight}>
      {prefix}
      {input}
      {suffix}
    </span>
  );
};

const money = (seconds: number, hourlyCost: number) =>
  formatMoney((seconds / 3600) * hourlyCost);

/**
 * Where the total comes from: one line per step, reviews times questions,
 * heaviest first, adding up to the total above it. It shows which choice on the
 * earlier steps costs the most.
 *
 * While there are work tools left to connect, two more lines say what connecting
 * them would take off and what the review could then be. The saving sits in the
 * same table as the cost, in the same units, as the next line of the sum.
 */
const TimeBill = ({
  preview,
  seconds,
  hourlyCost,
  savedSeconds,
  someConnected,
}: {
  preview: LoadPreview;
  seconds: number;
  hourlyCost: number | null;
  savedSeconds: number;
  someConnected: boolean;
}) => {
  const label = useLabel();
  const { nounPlural } = useProgramsSection();
  const names = stepNames(label);
  const steps = sortBy(preview.steps, (step) => -step.answers);
  const totalSeconds = preview.totalAnswers * seconds;
  const canSave = savedSeconds > 0;
  const sums = savingsSums(totalSeconds, savedSeconds, hourlyCost);

  return (
    // On a light gray panel, so the table reads as one block inside the card.
    <div className="rounded-lg bg-gray-50 p-2">
      {/* Every row is inset as far as the text in the blue saving rows, so the
          figures line up under one another whether a row is shaded or not.
          Separate borders, with each row's line drawn on its cells, so the
          blue saving rows can round their corners like the cards around
          them. */}
      <table className="w-full border-separate border-spacing-0 text-sm tabular-nums [&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:first-child]:pl-4 [&_th:last-child]:pr-4">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className="pb-2 text-left font-medium">Step</th>
            <th className="pb-2 text-right font-medium">{nounPlural}</th>
            {/* The "×" between reviews and questions has a column of its own,
                so both numbers stay under their headings. */}
            <th className="w-8 pb-2" />
            <th className="pb-2 text-right font-medium">Questions each</th>
            <th className="pb-2 text-right font-medium">Answers</th>
            <th className="pb-2 text-right font-medium">Time</th>
            {hourlyCost !== null && (
              <th className="pb-2 text-right font-medium">Cost</th>
            )}
          </tr>
        </thead>
        <tbody>
          {steps.map((step) => (
            <tr key={step.step} className="*:border-t">
              <td className="py-2.5">{names[step.step]}</td>
              <td className="py-2.5 text-right">{formatCount(step.reviews)}</td>
              <td className="py-2.5 text-center text-gray-400">×</td>
              <td className="py-2.5 text-right">
                {formatCount(step.questionsEach)}
              </td>
              <td className="py-2.5 text-right">{formatCount(step.answers)}</td>
              <td className="py-2.5 text-right">
                {formatDuration(step.answers * seconds)}
              </td>
              {hourlyCost !== null && (
                <td className="py-2.5 text-right">
                  {money(step.answers * seconds, hourlyCost)}
                </td>
              )}
            </tr>
          ))}
          <tr className="font-semibold *:border-t *:border-gray-300">
            <td className="pt-3">{canSave ? "Total today" : "Total"}</td>
            <td className="pt-3 text-right">
              {formatCount(preview.totalReviews)}
            </td>
            <td />
            <td />
            <td className="pt-3 text-right">
              {formatCount(preview.totalAnswers)}
            </td>
            <td className="pt-3 text-right">{formatDuration(totalSeconds)}</td>
            {hourlyCost !== null && (
              <td className="pt-3 text-right">
                {money(totalSeconds, hourlyCost)}
              </td>
            )}
          </tr>
          {canSave && (
            <>
              {/* A gap between the total and the saving, which a table row cannot
                  take as a margin. Hidden, so it is not read as a row. */}
              <tr aria-hidden="true">
                <td className="h-3" colSpan={7} />
              </tr>
              {/* The saving and what is left, on the same blue as the connected
                  tile, so the two read as one offer. The colour is on the cells,
                  which can round their corners; a row cannot. */}
              <tr className="text-blue-800 *:bg-blue-50 [&>td:first-child]:rounded-tl-md [&>td:last-child]:rounded-tr-md">
                <td className="pt-2.5" colSpan={5}>
                  {someConnected
                    ? "Connect the rest of your work tools"
                    : "Connect your work tools"}
                </td>
                <td className="pt-2.5 text-right">
                  − {formatDuration(sums.savedMinutes * 60)}
                </td>
                {hourlyCost !== null && (
                  <td className="pt-2.5 text-right">
                    − {formatMoney(sums.savedDollars ?? 0)}
                  </td>
                )}
              </tr>
              <tr className="font-semibold text-blue-800 *:bg-blue-50 [&>td:first-child]:rounded-bl-md [&>td:last-child]:rounded-br-md">
                <td className="pb-2.5 pt-2" colSpan={5}>
                  Could be
                </td>
                <td className="pb-2.5 pt-2 text-right">
                  {formatDuration(sums.afterMinutes * 60)}
                </td>
                {hourlyCost !== null && (
                  <td className="pb-2.5 pt-2 text-right">
                    {formatMoney(sums.afterDollars ?? 0)}
                  </td>
                )}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

/**
 * The same review twice, as it stands and with the remaining work tools
 * connected. Shown on its own in the summary, and above the people in the
 * breakdown.
 */
const SavingsTiles = ({
  totalSeconds,
  sums,
  hourlyCost,
}: {
  totalSeconds: number;
  sums: ReturnType<typeof savingsSums>;
  hourlyCost: number | null;
}) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    <div className="rounded-md border px-4 py-3">
      <div className="text-xs text-gray-500">Today</div>
      <div className="text-3xl font-semibold tabular-nums">
        {formatHours(sums.totalMinutes * 60)} h
      </div>
      <div className="text-sm text-gray-600 tabular-nums">
        {hourlyCost !== null
          ? `${formatMoney(sums.totalDollars ?? 0)} of working time`
          : `${formatDuration(totalSeconds)} of writing`}
      </div>
    </div>
    <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
      <div className="text-xs text-gray-500">
        With your work tools connected
      </div>
      <div className="text-3xl font-semibold text-blue-800 tabular-nums">
        {formatHours(sums.afterMinutes * 60)} h
      </div>
      <div className="text-sm text-gray-600 tabular-nums">
        {hourlyCost !== null
          ? `${formatMoney(sums.afterDollars ?? 0)} · ${formatDuration(sums.savedMinutes * 60)} and ${formatMoney(sums.savedDollars ?? 0)} back`
          : `${formatDuration(sums.savedMinutes * 60)} back`}
      </div>
    </div>
  </div>
);

/** How many people's before-and-after to show: enough to see who gains most. */
const maxPeople = 3;

/**
 * The same review twice, as it stands and with the remaining work tools
 * connected, then the heaviest writers' own before-and-after. "Marcus gets 21
 * minutes back" lands harder than a company total, and it shows that the
 * people who gain most are the ones writing self and manager reviews.
 */
const BeforeAndAfter = ({
  preview,
  seconds,
  hourlyCost,
  savedFraction,
  sums,
}: {
  preview: LoadPreview;
  seconds: number;
  hourlyCost: number | null;
  savedFraction: number;
  sums: ReturnType<typeof savingsSums>;
}) => {
  const totalSeconds = preview.totalAnswers * seconds;
  // Most self and manager writing first, as that is what the tools take off. A
  // stable sort, so the backend's heaviest-first order breaks the ties.
  const people = sortBy(
    preview.writers,
    (writer) => -writerToolHelpedQuestions(writer, preview),
  )
    .slice(0, maxPeople)
    .map((writer) => {
      const today = writer.questions * seconds;
      const saved =
        writerToolHelpedQuestions(writer, preview) * seconds * savedFraction;
      return { writer, today, after: today - saved };
    });
  const longest = Math.max(...people.map((person) => person.today), 1);

  return (
    <div>
      <SavingsTiles
        totalSeconds={totalSeconds}
        sums={sums}
        hourlyCost={hourlyCost}
      />

      <ul className="mt-5 space-y-2.5 tabular-nums">
        {people.map(({ writer, today, after }, index) => (
          <li
            key={writer.user?.id ?? `hidden-${index}`}
            className="grid grid-cols-[10rem_1fr_9rem] items-center gap-3 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Avatar user={writer.user} size="5" />
              <span className="truncate">{writer.user?.name ?? "Someone"}</span>
            </span>
            <div className="relative h-2.5">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gray-200"
                style={{ width: `${(today / longest) * 100}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-blue-600"
                style={{ width: `${(after / longest) * 100}%` }}
              />
            </div>
            <span className="text-right text-gray-600">
              {formatDuration(today)} → {formatDuration(after)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-gray-200" />
          Today
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" />
          With your work tools connected
        </span>
      </div>
    </div>
  );
};

/**
 * How much work the review on this form will ask of people, before it is
 * published: how long one review is, what the largest group writes, what the busiest
 * person writes, and how the rest spread between the two.
 *
 * Runs as soon as the tab is shown, with no button to open it: choosing this tab
 * is the admin asking the question.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 * The time per question is a guess the admin makes on the screen, so changing it
 * multiplies the answer already in hand instead of asking again.
 */
const ProgramFormReadiness = ({
  proposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const { noun } = useProgramsSection();
  const [secondsPerQuestion, setSecondsPerQuestion] = useState(
    defaultSecondsPerQuestion,
  );

  const { preview, loading, error, refusal, waitingForASelection } =
    useProgramFormLoadPreview(proposedComplianceProgram);
  const hasFigures =
    !waitingForASelection &&
    preview !== undefined &&
    preview.writers.length > 0;

  const [hourlyCost, setHourlyCost] = useState<number | null>(
    defaultHourlyCost,
  );
  const [savingsPercent, setSavingsPercent] = useState(
    defaultToolSavingsPercent,
  );
  // Only what is still to connect can save anything. The estimate is for all four
  // categories, so each one left to connect counts for a quarter of it.
  const { categories, loaded: toolsLoaded } = useReadinessTools(
    proposedComplianceProgram.organizationId,
  );
  const unconnected = categories.filter((category) => !category.connected);
  const savedFraction =
    (savingsPercent / 100) * (unconnected.length / categories.length);
  const savedSeconds = preview
    ? toolHelpedAnswers(preview) * secondsPerQuestion * savedFraction
    : 0;
  const connectedCount = categories.length - unconnected.length;
  const sums = savingsSums(
    preview ? preview.totalAnswers * secondsPerQuestion : 0,
    savedSeconds,
    hourlyCost,
  );

  // The summary comes first: the one saving, and a way into the figures behind
  // it. It only makes sense while there is a saving to claim; otherwise the
  // breakdown is all there is to show.
  const [showBreakdown, setShowBreakdown] = useState(false);
  const showSummary =
    hasFigures && preview !== undefined && savedSeconds > 0 && !showBreakdown;
  // Which view to open on depends on what is connected, so neither is shown
  // until that is known: a saving claimed for a tool that turns out to be
  // connected would flash up and then vanish.
  const waitingForTools = hasFigures && !toolsLoaded;

  // The guesses every figure rests on. Rendered by whichever view is showing,
  // each box starting from the value the other view left it at.
  const minutesBox = (unitsOutside = false) => (
    <AssumptionBox
      unitsOutside={unitsOutside}
      ariaLabel="Minutes per question"
      value={secondsPerQuestion}
      parse={parseMinutesPerQuestion}
      format={(value) => String(value / 60)}
      onChange={(value) => value !== null && setSecondsPerQuestion(value)}
      restoreOnBlur
      suffix={"\u00a0min"}
    />
  );
  const costBox = (unitsOutside = false) => (
    <AssumptionBox
      unitsOutside={unitsOutside}
      prefix="$"
      suffix="/hr"
      ariaLabel="Hourly cost"
      value={hourlyCost}
      parse={parseHourlyCost}
      format={String}
      onChange={setHourlyCost}
    />
  );
  const savingBox = (unitsOutside = false) => (
    <AssumptionBox
      unitsOutside={unitsOutside}
      ariaLabel="Saving from work tools"
      value={savingsPercent}
      parse={parsePercent}
      format={String}
      onChange={(value) => value !== null && setSavingsPercent(value)}
      restoreOnBlur
      suffix="%"
    />
  );

  // A failed request must never read as "nobody writes anything", and must
  // never leave old figures looking freshly checked. Both views show it.
  const errorNote = error && (
    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
      {refusal ||
        (preview
          ? "This could not be refreshed, so it may be out of date. Reload the page to try again."
          : "This could not be loaded. Reload the page to try again.")}
    </div>
  );
  // Said out loud, because a figure that leaves the peers out would otherwise
  // read as a review with no peer work in it. Both views show it.
  const peerNote = preview?.peerCountUnknown && (
    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
      Peers are chosen after this {noun.toLowerCase()} starts, so their reviews
      are not counted here.
    </div>
  );

  return (
    <section>
      <div className="text-lg font-semibold">
        {noun} Readiness and Effort Analysis
      </div>
      {/* One card per question the admin is asking, rather than one card
          holding them all. */}
      <div className="mt-3 space-y-4">
        {waitingForTools ? (
          <div className="rounded-md border bg-white p-6">
            <Loading size="5" mini />
          </div>
        ) : showSummary ? (
          <div className="rounded-md border bg-white p-6">
            <div className="flex items-center gap-3">
              <div className="tf-header-base-dark">
                Connecting more tools could save as much as{" "}
                {sums.savedDollars !== null && (
                  <>
                    <span className="text-amber-600 tabular-nums">
                      {formatMoney(sums.savedDollars)}
                    </span>{" "}
                    and{" "}
                  </>
                )}
                <span className="text-amber-600 tabular-nums">
                  {formatDuration(sums.savedMinutes * 60)}
                </span>
              </div>
              {loading && <Loading size="4" mini />}
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm text-gray-600">
              <span>Assuming</span>
              {minutesBox()}
              <span>per question, at an avg. of</span>
              <span className="inline-flex items-baseline">{costBox()},</span>
              <span>and that tools save</span>
              {savingBox()}
              <span>of self and manager writing.</span>
            </div>
            {(errorNote || peerNote) && (
              <div className="mt-4 space-y-3">
                {errorNote}
                {peerNote}
              </div>
            )}
            <div className="mt-4">
              <SavingsTiles
                totalSeconds={preview.totalAnswers * secondsPerQuestion}
                sums={sums}
                hourlyCost={hourlyCost}
              />
            </div>
            <button
              type="button"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
              onClick={() => setShowBreakdown(true)}
            >
              <TopicflowIcon type="eye" className="h-4 w-4" />
              Show me the breakdown
            </button>
          </div>
        ) : (
          <>
            {/* An even gap between whatever the card holds, as it has no heading
            to sit under. */}
            <div className="flex flex-col gap-3 rounded-md border bg-white p-6">
              {hasFigures && preview && (
                <div>
                  {/* The whole card in one sentence, its two figures in amber. */}
                  <div className="flex items-center gap-3">
                    <div className="tf-header-base-dark">
                      Your {noun.toLowerCase()} could take{" "}
                      <span className="text-amber-600 tabular-nums">
                        {formatHours(sums.totalMinutes * 60)}{" "}
                        {sums.totalMinutes === 60 ? "hour" : "hours"}
                      </span>{" "}
                      to write
                      {sums.totalDollars !== null && (
                        <>
                          {" "}
                          and cost you{" "}
                          <span className="text-amber-600 tabular-nums">
                            {formatMoney(sums.totalDollars)}
                          </span>
                        </>
                      )}
                    </div>
                    {loading && <Loading size="4" mini />}
                  </div>
                  {/* The two guesses the total and the cost rest on, right under
                  the number they make, each in a box the admin can change. */}
                  <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-600">
                    <span>Assuming</span>
                    {minutesBox(true)}
                    <span>per question at an average of</span>
                    {costBox(true)}
                    {hourlyCost === null && (
                      <span className="text-gray-500">
                        Type a cost to see this as money.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {waitingForASelection ? (
                <div className="text-sm text-gray-500">
                  Choose who takes part on the Participants tab first.
                </div>
              ) : (
                <>
                  {errorNote}
                  {peerNote}
                  {preview ? (
                    hasFigures ? (
                      <div className="pt-2">
                        <TimeBill
                          preview={preview}
                          seconds={secondsPerQuestion}
                          hourlyCost={hourlyCost}
                          savedSeconds={savedSeconds}
                          someConnected={connectedCount > 0}
                        />
                      </div>
                    ) : (
                      // Peers left uncounted are the reason, when they are; the
                      // note above says so, and this advice would be wrong.
                      !preview.peerCountUnknown && (
                        <div className="text-sm text-gray-500">
                          Nobody has questions to answer yet. Turn on a step on
                          the Workflow tab and choose its questions on the
                          Questions tab.
                        </div>
                      )
                    )
                  ) : (
                    !error && (
                      <div>
                        <Loading size="5" mini />
                      </div>
                    )
                  )}
                </>
              )}
            </div>
            {/* What connecting the rest of the work tools would take off. Only
            while there is something left to claim. */}
            {hasFigures && preview && savedSeconds > 0 && (
              <div className="rounded-md border bg-white p-6">
                <div className="tf-header-base-dark">
                  Connecting more tools could result in{" "}
                  <span className="text-amber-600 tabular-nums">
                    {formatDuration(sums.savedMinutes * 60)}
                  </span>{" "}
                  less
                  {sums.savedDollars !== null && (
                    <>
                      {" "}
                      and{" "}
                      <span className="text-amber-600 tabular-nums">
                        {formatMoney(sums.savedDollars)}
                      </span>{" "}
                      back
                    </>
                  )}
                </div>
                {/* The guess the saving rests on, under the heading that makes the
                claim. It also sets the saving lines in the table above. */}
                <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-600">
                  <span>Assuming connecting your work tools saves</span>
                  {savingBox(true)}
                  <span>of self and manager writing.</span>
                </div>
                <div className="mt-4">
                  <BeforeAndAfter
                    preview={preview}
                    seconds={secondsPerQuestion}
                    hourlyCost={hourlyCost}
                    savedFraction={savedFraction}
                    sums={sums}
                  />
                </div>
              </div>
            )}
          </>
        )}
        {/* The tools that would do it, with Connect beside each one missing.
        Shown in both views. Once every one is connected, it only says what
            people draw on. Not until the connected types are known, so a tool
            connected through an integration row never flashes up as missing. */}
        {toolsLoaded && (
          <div className="rounded-md border bg-white p-6">
            <div className="tf-header-base-dark">
              {unconnected.length > 0
                ? "Here are the areas we recommend you connect"
                : "What people will have to draw on"}
            </div>
            <div className="mt-3">
              <ReadinessIntegrations
                organizationId={proposedComplianceProgram.organizationId}
              />
            </div>
          </div>
        )}
        {/* Back to the summary, for anyone comparing the two. */}
        {showBreakdown && hasFigures && savedSeconds > 0 && (
          <button
            type="button"
            className="rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
            onClick={() => setShowBreakdown(false)}
          >
            Hide the breakdown
          </button>
        )}
      </div>
    </section>
  );
};

export default ProgramFormReadiness;
